/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { BellOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AlertConfig } from '@/client';
import { ContentContainer } from '@/components/ContentContainer';
import { PageHeader } from '@/components/PageHeader';
import { PaginatedTable } from '@/components/PaginatedTable';
import { QueryWrapper } from '@/components/QueryWrapper';
import { useAlertConfigsQuery } from '@/hooks/query/useAlertConfigsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Dropdown, MenuProps } from '@/primitives/Dropdown';
import { Select } from '@/primitives/Select';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

import { destinationDisplayNames, DestinationType } from './constants';
import { CreateAlertWizard } from './CreateAlertWizard';
import './NotificationManagementPage.css';

dayjs.extend(utc);

const destinationOptions: { label: string; value: DestinationType }[] = [
  { label: 'Exabeam', value: 'exabeam' },
  { label: 'Slack', value: 'slack' },
  { label: 'Splunk', value: 'splunk' },
];

const statusColors: Record<string, string> = {
  active: '#4caf50',
  suspended: '#d4a017',
  error: '#e53935',
};

export function NotificationManagementPage() {
  const { t } = useMimicTranslation('notificationManagement');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [selectedDestination, setSelectedDestination] = useState<DestinationType | undefined>();
  const alertConfigsQuery = useAlertConfigsQuery({ type: 'inMemory' });
  const navigate = useNavigate();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const handleDestinationSelect = (value: DestinationType) => {
    setSelectedDestination(value);
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setSelectedDestination(undefined);
  };

  const handleAlertClick = (alert: AlertConfig) => {
    navigate(`/tenants/${tenantID}/notifications/management/${alert.id}`);
  };

  const newConnectionMenuItems: MenuProps['items'] = destinationOptions.map((opt) => ({
    key: opt.value,
    label: opt.label,
    onClick: () => handleDestinationSelect(opt.value),
  }));

  const columns: ColumnsType<AlertConfig> = [
    {
      title: t('table.connectionName'),
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (name: string, record: AlertConfig) => (
        <Text
          type="link"
          size="md"
          style={{ cursor: 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            handleAlertClick(record);
          }}
        >
          {name}
        </Text>
      ),
    },
    {
      title: t('table.destination'),
      dataIndex: 'type',
      key: 'type',
      sorter: true,
      render: (type: string) => destinationDisplayNames[type as DestinationType] || type,
    },
    {
      title: t('table.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status: string) => {
        const color = statusColors[status] || statusColors.suspended;
        const label = status.charAt(0).toUpperCase() + status.slice(1);
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: color,
                flexShrink: 0,
              }}
            />
            {label}
          </span>
        );
      },
    },
    {
      title: t('table.created'),
      dataIndex: 'dateCreated',
      key: 'dateCreated',
      sorter: true,
      render: (date: string) => dayjs(date).utc().format('MMM DD, YYYY – HH:mm:ss'),
    },
  ];

  return (
    <ContentContainer width="100%">
      <Container full style={{ width: '100%' }}>
        <PageHeader title={t('title')} text={t('subtitle')} level={2}>
          <Dropdown menu={{ items: newConnectionMenuItems }}>
            <Button type="primary">{t('newConnection')}</Button>
          </Dropdown>
        </PageHeader>

        <QueryWrapper queryResult={alertConfigsQuery}>
          {(alertConfigsData) => {
            const hasAlerts = alertConfigsData.data.length > 0;

            return hasAlerts ? (
              <div className="notification-management-alerts-list">
                <PaginatedTable
                  data={alertConfigsData}
                  columns={columns}
                  rowKey="id"
                  onParamsChange={alertConfigsQuery.updateQueryParams}
                  onRow={(record) => ({
                    onClick: () => handleAlertClick(record),
                    style: { cursor: 'pointer' },
                  })}
                  hidePaginationInfo
                  data-testid="alert-configs-table"
                />
              </div>
            ) : (
              <div className="notification-management-setup">
                <BellOutlined className="notification-management-setup-icon" />
                <Title level={3}>{t('setupTitle')}</Title>
                <Text type="subtle">{t('setupSubtitle')}</Text>
                <Select
                  className="notification-management-destination-select"
                  placeholder={t('selectDestination')}
                  options={destinationOptions}
                  onChange={handleDestinationSelect}
                  value={selectedDestination}
                  data-testid="destination-select"
                />
              </div>
            );
          }}
        </QueryWrapper>
      </Container>

      {wizardOpen && selectedDestination && (
        <CreateAlertWizard destinationType={selectedDestination} onClose={handleWizardClose} />
      )}
    </ContentContainer>
  );
}
